import { Head, useForm, router } from "@inertiajs/react"
import { useRef, useState, type FormEvent } from "react"
import { track } from "@/lib/analytics"
import DashboardLayout from "@/components/dashboard/layout"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import type { User } from "@/types"

export default function ProfileShow({ user }: { user: User }) {
  const initials =
    user.name
      ?.split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2) || "?"

  return (
    <DashboardLayout>
      <Head title="Profile | QuiteRight" />

      <div className="mb-8">
        <h1 className="font-display text-2xl font-semibold tracking-tight text-charcoal">
          Profile
        </h1>
        <p className="mt-1 text-warm-grey">
          Manage your personal details and profile photo.
        </p>
      </div>

      <div className="max-w-xl space-y-8">
        <AvatarSection user={user} initials={initials} />
        <DetailsSection user={user} />
        {user.has_password && <PasswordSection />}
      </div>
    </DashboardLayout>
  )
}

function AvatarSection({ user, initials }: { user: User; initials: string }) {
  const fileRef = useRef<HTMLInputElement>(null)
  const [preview, setPreview] = useState<string | null>(null)

  function handleUpload(file: File) {
    track("avatar_uploaded")
    setPreview(URL.createObjectURL(file))
    const formData = new FormData()
    formData.append("avatar", file)
    router.patch("/dashboard/profile", formData as never, {
      forceFormData: true,
      preserveScroll: true,
      onSuccess: () => setPreview(null),
    })
  }

  function handleRemove() {
    setPreview(null)
    router.patch("/dashboard/profile", { remove_avatar: "true" } as never, {
      preserveScroll: true,
    })
  }

  return (
    <div className="rounded-xl border border-stone/40 bg-white p-6">
      <h2 className="mb-4 font-display text-lg font-semibold text-charcoal">Photo</h2>
      <div className="flex items-center gap-5">
        <Avatar className="size-20 border-2 border-stone/30">
          <AvatarImage src={preview || user.avatar_url || undefined} />
          <AvatarFallback className="bg-stone/40 text-lg font-medium">{initials}</AvatarFallback>
        </Avatar>
        <div className="space-y-2">
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => fileRef.current?.click()}
            >
              Upload photo
            </Button>
            {user.avatar_url && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-warm-grey hover:text-charcoal"
                onClick={handleRemove}
              >
                Remove
              </Button>
            )}
          </div>
          <p className="text-xs text-warm-grey">PNG, JPG or WebP. Max 5 MB.</p>
        </div>
      </div>
      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (file) handleUpload(file)
          e.target.value = ""
        }}
      />
    </div>
  )
}

function DetailsSection({ user }: { user: User }) {
  const { data, setData, patch, processing, errors } = useForm({
    user: {
      name: user.name || "",
      email: user.email || "",
    },
  })

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    track("profile_updated")
    patch("/dashboard/profile")
  }

  return (
    <form onSubmit={handleSubmit}>
      <div className="rounded-xl border border-stone/40 bg-white p-6">
        <h2 className="mb-4 font-display text-lg font-semibold text-charcoal">
          Personal details
        </h2>
        <div className="space-y-4">
          <div>
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              value={data.user.name}
              onChange={(e) => setData("user", { ...data.user, name: e.target.value })}
              className="mt-1.5"
              required
            />
            {errors["user.name"] && (
              <p className="mt-1 text-xs text-red">{errors["user.name"]}</p>
            )}
          </div>
          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={data.user.email}
              onChange={(e) => setData("user", { ...data.user, email: e.target.value })}
              className="mt-1.5"
              required
            />
            {errors["user.email"] && (
              <p className="mt-1 text-xs text-red">{errors["user.email"]}</p>
            )}
          </div>
        </div>
        <Button
          type="submit"
          disabled={processing}
          className="mt-6 bg-red text-white hover:bg-red-deep"
        >
          {processing ? "Saving..." : "Save details"}
        </Button>
      </div>
    </form>
  )
}

function PasswordSection() {
  const { data, setData, patch, processing, errors, reset } = useForm({
    current_password: "",
    password: "",
    password_confirmation: "",
  })

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    patch("/dashboard/profile", {
      onSuccess: () => reset(),
    })
  }

  return (
    <form onSubmit={handleSubmit}>
      <div className="rounded-xl border border-stone/40 bg-white p-6">
        <h2 className="mb-4 font-display text-lg font-semibold text-charcoal">
          Change password
        </h2>
        <div className="space-y-4">
          <div>
            <Label htmlFor="current_password">Current password</Label>
            <Input
              id="current_password"
              type="password"
              value={data.current_password}
              onChange={(e) => setData("current_password", e.target.value)}
              className="mt-1.5"
              required
            />
            {errors.current_password && (
              <p className="mt-1 text-xs text-red">{errors.current_password}</p>
            )}
          </div>
          <div>
            <Label htmlFor="password">New password</Label>
            <Input
              id="password"
              type="password"
              value={data.password}
              onChange={(e) => setData("password", e.target.value)}
              className="mt-1.5"
              required
              minLength={6}
            />
            {errors.password && (
              <p className="mt-1 text-xs text-red">{errors.password}</p>
            )}
          </div>
          <div>
            <Label htmlFor="password_confirmation">Confirm new password</Label>
            <Input
              id="password_confirmation"
              type="password"
              value={data.password_confirmation}
              onChange={(e) => setData("password_confirmation", e.target.value)}
              className="mt-1.5"
              required
              minLength={6}
            />
            {errors.password_confirmation && (
              <p className="mt-1 text-xs text-red">{errors.password_confirmation}</p>
            )}
          </div>
        </div>
        <Button
          type="submit"
          disabled={processing}
          className="mt-6 bg-red text-white hover:bg-red-deep"
        >
          {processing ? "Updating..." : "Update password"}
        </Button>
      </div>
    </form>
  )
}
